import * as THREE from 'three';

import { AISLE_WIDTH, BOOTH } from '../config/booth';

export interface DragTarget {
  kind: 'fixture' | 'staff';
  id: string;
}

export interface PlanControls {
  camera: THREE.OrthographicCamera;
  setEnabled(on: boolean): void;
  /** Objects whose roots carry userData.kind/id and can be dragged. */
  setDraggables(roots: THREE.Object3D[]): void;
  onDrag: ((target: DragTarget, x: number, z: number) => void) | null;
  onDrop: ((target: DragTarget) => void) | null;
}

/** Top-down plan: an orthographic camera over the booth and its two aisles, with drag to rearrange. */
export function createPlanControls(canvas: HTMLCanvasElement): PlanControls {
  // Frame both aisles plus a little of the neighbours; the panel covers the left edge.
  const halfHeight = (BOOTH.depth + AISLE_WIDTH + 14) / 2;
  const centre = {
    x: BOOTH.width / 2 + 1,
    z: (BOOTH.depth + AISLE_WIDTH) / 2 - 1,
  };
  const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 200);
  camera.userData['halfHeight'] = halfHeight;
  camera.position.set(centre.x, 60, centre.z);
  camera.up.set(0, 0, -1);
  camera.lookAt(centre.x, 0, centre.z);

  const ray = new THREE.Raycaster();
  const floor = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
  const ndc = new THREE.Vector2();
  const hit = new THREE.Vector3();
  let enabled = false;
  let draggables: THREE.Object3D[] = [];
  let active: { target: DragTarget; dx: number; dz: number } | null = null;

  const api: PlanControls = {
    camera,
    onDrag: null,
    onDrop: null,
    setEnabled(on) {
      enabled = on;
      active = null;
    },
    setDraggables(roots) {
      draggables = roots;
    },
  };

  function toFloor(e: PointerEvent): THREE.Vector3 | null {
    const r = canvas.getBoundingClientRect();
    ndc.set(
      ((e.clientX - r.left) / r.width) * 2 - 1,
      -((e.clientY - r.top) / r.height) * 2 + 1,
    );
    ray.setFromCamera(ndc, camera);
    return ray.ray.intersectPlane(floor, hit) ? hit : null;
  }

  function rootOf(o: THREE.Object3D): THREE.Object3D | null {
    let cur: THREE.Object3D | null = o;
    while (cur) {
      if (cur.userData['kind']) return cur;
      cur = cur.parent;
    }
    return null;
  }

  canvas.addEventListener('pointerdown', (e) => {
    if (!enabled) return;
    const p = toFloor(e);
    if (!p) return;
    const hits = ray.intersectObjects(draggables, true);
    const root = hits[0] ? rootOf(hits[0].object) : null;
    if (!root) return;
    active = {
      target: {
        kind: root.userData['kind'] as DragTarget['kind'],
        id: root.userData['id'] as string,
      },
      dx: root.position.x - p.x,
      dz: root.position.z - p.z,
    };
    canvas.setPointerCapture(e.pointerId);
    canvas.style.cursor = 'grabbing';
  });
  canvas.addEventListener('pointermove', (e) => {
    if (!enabled) return;
    if (!active) {
      const p = toFloor(e);
      const over = p
        ? ray.intersectObjects(draggables, true).length > 0
        : false;
      canvas.style.cursor = over ? 'grab' : 'default';
      return;
    }
    const p = toFloor(e);
    if (!p) return;
    api.onDrag?.(active.target, p.x + active.dx, p.z + active.dz);
  });
  const end = (e: PointerEvent) => {
    if (!active) return;
    api.onDrop?.(active.target);
    active = null;
    canvas.style.cursor = 'default';
    if (canvas.hasPointerCapture(e.pointerId))
      canvas.releasePointerCapture(e.pointerId);
  };
  canvas.addEventListener('pointerup', end);
  canvas.addEventListener('pointercancel', end);

  return api;
}
